import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { connect } from 'react-redux';
import 'react-quill/dist/quill.snow.css';
import { getSystemsOverview, clearSystemsForTable, setProblemsConfirmed } from '../../stores/actions/actions';
import { FilterService, FilterMatchMode } from 'primereact/api';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import Power from "@material-ui/icons/Power";
import BatteryFull from "@material-ui/icons/BatteryFull";
import PanTool from "@material-ui/icons/PanTool";
import Computer from "@material-ui/icons/Computer";
import Check from "@material-ui/icons/Check";
import Close from "@material-ui/icons/Close";
import SwapHoriz from "@material-ui/icons/SwapHoriz";
import Opacity from "@material-ui/icons/Opacity";
import Build from "@material-ui/icons/Build";
import { Tag } from 'primereact/tag';
import { Link } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { round } from 'lodash';
import { Button } from 'primereact/button';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import MapLoading from '../Dashboard/MapLoading';

interface SystemsTableProps {
    systems: any;
    systemsForTableLoading: boolean;
    getSystemsOverview: () => void;
    clearSystemsForTable: () => void;
    setProblemsConfirmed: (sysId: number, confirmed: boolean) => void;
}

// Toggleable columns (excludes "name", which is always shown).
// change the storage key version (v1 -> v2) if you ever rename/remove a field,
// so stale localStorage entries referencing dead fields get discarded.
const TOGGLEABLE_COLUMNS = [
    { field: 'syncTimestamp', label: 'Sync' },
    { field: 'lastMeasurementTimestamp', label: 'Last m.' },
    { field: 'lastSMSTimestamp', label: 'Last SMS' },
    { field: 'lostDevicesCount', label: 'Lost' },
    { field: 'sensorErrorsCount', label: 'Sens. err.' },
    { field: 'sensorErrorNodesCount', label: 'Sens. err. cnt' },
    { field: 'mode', label: 'Mode' },
    { field: 'power', label: 'Power' },
    { field: 'minBatteryPercentage', label: 'Battery' },
    { field: 'maxDeflection', label: 'Defl.' },
    { field: 'measurementPeriod', label: 'M. Per.' },
    { field: 'l1Count', label: 'L1' },
    { field: 'l2Count', label: 'L2' },
    { field: 'l3Count', label: 'L3' },
    { field: 'l4Count', label: 'L4' },
    { field: 'obstructedCount', label: 'Obstr.' },
    { field: 'operationalActiveCount', label: 'Op./Act' },
    { field: 'mdiCount', label: 'MDI' },
    { field: 'mdlCount', label: 'MDL' },
    { field: 'iadCount', label: 'IAD' },
    { field: 'adCount', label: 'AD' },
    { field: 'deviceCount', label: 'Gen Devices' },
    { field: 'serviceModeNow', label: 'S. Mode' },
    { field: 'overdueMeasurementsCount', label: 'Ov. M.' },
    { field: 'notificationsEmailOn', label: 'E-mail' },
    { field: 'notificationsPushOn', label: 'Push' },
    { field: 'notificationsSmsOn', label: 'SMS' },
    { field: 'routersCount', label: 'Routers' },
    { field: 'simcardsDataCount', label: 'SIM cards Data' },
    { field: 'simcardsSMSCount', label: 'SIM cards SMS' },
    { field: 'en91Count', label: 'AA' },
    { field: 'en92Count', label: 'AAA' },
    { field: 'en95Count', label: 'D' },
    { field: 'rainstormModeNow', label: 'Rainstorm mode' },
    { field: 'dailyMeasurement', label: 'Daily meas.' },
    { field: 'maintenanceProblems', label: 'Maintenance' },
];

// Shown by default when a user has no saved column preference yet.
// Everything else in TOGGLEABLE_COLUMNS starts hidden and can be turned on
// via the column picker.
const DEFAULT_VISIBLE_FIELDS = [
    'syncTimestamp',
    'lastMeasurementTimestamp',
    'lastSMSTimestamp',
    'lostDevicesCount',
    'sensorErrorsCount',
    'mode',
    'power',
    'measurementPeriod',
    'serviceMode',
    'overdueMeasurementsCount',
    'maintenanceProblems'
];

const COLUMN_VISIBILITY_STORAGE_KEY = 'systemsTable.visibleColumns.v3';

const GENERATION_OPTIONS = [
    { label: 'All device generations', value: 'all' },
    { label: 'Gen 10', value: '10' },
    { label: 'Gen 12', value: '12' },
    { label: 'Gen 16', value: '16' },
    { label: 'Gen 17', value: '17' },
    { label: 'Gen 20', value: '20' },
    { label: 'Gen 25', value: '25' },
];

// Backend only emits a generation bucket in genBreakdown when a system has at
// least one device of that generation (GROUP BY ROLLUP doesn't produce empty
// groups). Systems with zero devices of a selected generation are simply
// missing that key entirely - fall back to this so counts always render as
// 0 instead of a blank cell.
const EMPTY_GEN_BREAKDOWN = {
    deviceCount: 0,
    lostDevicesCount: 0,
    overdueMeasurementsCount: 0,
    l1Count: 0,
    l2Count: 0,
    l3Count: 0,
    l4Count: 0,
    obstructedCount: 0,
    operationalActiveCount: 0,
    operationalInactiveCount: 0,
    nonoperationalCount: 0,
    mdiCount: 0,
    mdlCount: 0,
    iadCount: 0,
    adCount: 0,
};

// Columns whose values come from genBreakdown and therefore change meaning
// depending on the selected generation - used to visually flag them when a
// specific generation (not "all") is selected.
const GEN_FILTERED_FIELDS = [
    'lostDevicesCount',
    'overdueMeasurementsCount',
    'l1Count',
    'l2Count',
    'l3Count',
    'l4Count',
    'obstructedCount',
    'displayedNodeStateCount',
    'mdiCount',
    'mdlCount',
    'iadCount',
    'adCount',
    'deviceCount',
];

// Default filter shape - extracted to module scope so it can be reused both
// as the initial state and as the baseline for diffing against the URL
// (only non-default filter values get written to the URL, to keep it short).
const DEFAULT_FILTERS = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    power: { value: null, matchMode: FilterMatchMode.EQUALS },
    mode: { value: null, matchMode: FilterMatchMode.EQUALS },
    serviceModeNow: { value: null, matchMode: FilterMatchMode.EQUALS },
    rainstormModeNow: { value: null, matchMode: FilterMatchMode.EQUALS },
    notificationsEmailOn: { value: null, matchMode: FilterMatchMode.EQUALS },
    notificationsPushOn: { value: null, matchMode: FilterMatchMode.EQUALS },
    notificationsSmsOn: { value: null, matchMode: FilterMatchMode.EQUALS },
    lostDevicesCount: { value: null, matchMode: 'count_filter' },
    overdueMeasurementsCount: { value: null, matchMode: 'count_filter' },
    sensorErrorsCount: { value: null, matchMode: 'count_filter' },
    sensorErrorNodesCount: { value: null, matchMode: 'count_filter' },
};

// Value type per filterable field - needed to correctly parse booleans back
// out of the URL (query params are always strings). matchMode is fixed per
// field at the column-definition level and never changes at runtime, so it
// doesn't need to be persisted at all - only the value does.
const FILTER_FIELD_TYPES: Record<string, 'string' | 'boolean'> = {
    name: 'string',
    power: 'string',
    mode: 'string',
    serviceModeNow: 'boolean',
    rainstormModeNow: 'boolean',
    notificationsEmailOn: 'boolean',
    notificationsPushOn: 'boolean',
    notificationsSmsOn: 'boolean',
    lostDevicesCount: 'string',
    overdueMeasurementsCount: 'string',
    sensorErrorsCount: 'string',
    sensorErrorNodesCount: 'string',
};

const getUrlParams = () => new URLSearchParams(window.location.search);

const sameFieldSet = (a: string[], b: string[]) =>
    a.length === b.length && a.every((f) => b.includes(f));

// Builds the filters object by reading one flat f_<fieldName> param per
// field (e.g. f_name=wi, f_lostDevicesCount=nonzero) instead of a single
// JSON blob - shorter and human-readable in the address bar.
const buildFiltersFromUrl = (): typeof DEFAULT_FILTERS => {
    const urlParams = getUrlParams();
    const result: any = { ...DEFAULT_FILTERS };
    Object.keys(DEFAULT_FILTERS).forEach((key) => {
        if (key === 'global') return; // driven by the separate `q` param via globalFilter
        const raw = urlParams.get(`f_${key}`);
        if (raw === null) return;
        const fieldType = FILTER_FIELD_TYPES[key];
        const value = fieldType === 'boolean' ? raw === 'true' : raw;
        result[key] = { value, matchMode: (DEFAULT_FILTERS as any)[key].matchMode };
    });
    return result;
};

const SystemsTable = ({
    systems,
    systemsForTableLoading,
    getSystemsOverview,
    clearSystemsForTable,
    setProblemsConfirmed
}: SystemsTableProps) => {
    
    const dt = useRef(null);
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const topScrollRef = useRef<HTMLDivElement>(null);
    const topScrollContentRef = useRef<HTMLDivElement>(null);
    const grayDateStyle = {color: 'white', backgroundColor: 'gray', padding: '6px', borderRadius: '6px', textAlign: 'center'};
    const blackDateStyle = {color: 'white', backgroundColor: 'black', padding: '6px', borderRadius: '6px', textAlign: 'center'};
    const redDateStyle = {color: 'white', backgroundColor: '#c0392b', padding: '6px', borderRadius: '6px', textAlign: 'center'};
    const orangeDateStyle = {color: 'white', backgroundColor: 'orange', padding: '6px', borderRadius: '6px', textAlign: 'center'};
    const yellowDateStyle = {color: 'white', backgroundColor: 'gold', padding: '6px', borderRadius: '6px', textAlign: 'center'};
    const [nodeStateField, setNodeStateField] = useState<string>(() => getUrlParams().get('nodeState') || 'operationalActiveCount');
    const [simcardDataStateField, setSimcardDataStateField] = useState<string>(() => getUrlParams().get('simcardDataState') || 'simcardsDataCount');
    const [simcardSMSStateField, setSimcardSMSStateField] = useState<string>(() => getUrlParams().get('simcardSMSState') || 'simcardsSMSCount');
    const [selectedGeneration, setSelectedGeneration] = useState<string>(() => getUrlParams().get('gen') || 'all');
    const [globalFilter, setGlobalFilter] = useState<string | null>(() => getUrlParams().get('q') || null);
    const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());
    const [first, setFirst] = useState<number>(() => {
        const v = getUrlParams().get('first');
        const parsed = v ? parseInt(v, 10) : 0;
        return Number.isNaN(parsed) ? 0 : parsed;
    });
    const [rows, setRows] = useState<number>(() => {
        const v = getUrlParams().get('rows');
        const parsed = v ? parseInt(v, 10) : 25;
        return Number.isNaN(parsed) ? 25 : parsed;
    });
    const [sortField, setSortField] = useState<string | undefined>(() => getUrlParams().get('sortField') || undefined);
    const [sortOrder, setSortOrder] = useState<1 | -1 | undefined>(() => {
        const v = getUrlParams().get('sortOrder');
        if (v === '1') return 1;
        if (v === '-1') return -1;
        return undefined;
    });

    const [visibleFields, setVisibleFields] = useState<string[]>(() => {
        const urlCols = getUrlParams().get('cols');
        if (urlCols) {
            const fromUrl = urlCols.split(',').filter((f) => TOGGLEABLE_COLUMNS.some(c => c.field === f));
            if (fromUrl.length > 0) return fromUrl;
        }
        try {
            const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // guard against stale fields from a renamed/removed column
                const validFields = parsed.filter((f: string) =>
                    TOGGLEABLE_COLUMNS.some(c => c.field === f)
                );
                if (validFields.length > 0) return validFields;
            }
        } catch (e) {
            console.error('Failed to load column visibility settings', e);
        }
        return DEFAULT_VISIBLE_FIELDS; // default: curated core columns, not everything
    });

    useEffect(() => {
        try {
            localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(visibleFields));
        } catch (e) {
            console.error('Failed to save column visibility settings', e);
        }
    }, [visibleFields]);

    useEffect(() => {
        setLoadingIds(new Set());
    }, [systems]);

    useEffect(() => {
        getSystemsOverview();
        return () => {
            clearSystemsForTable();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Merge in the counts for the currently-selected generation (from genBreakdown),
    // then pick out whichever field the node-state dropdown wants displayed,
    // then attach per-row loading state. All derived off the raw `systems` data
    // so DataTable's `value` prop always gets a fresh array/objects when any of
    // these change, avoiding PrimeReact's internal render-skipping.
    const systemsWithDerivedData = useMemo(() => {
        if (!systems) return systems;
        return systems.map((s: any) => {
            const breakdown = s.genBreakdown?.[selectedGeneration] ?? EMPTY_GEN_BREAKDOWN;
            return {
                ...s,
                ...breakdown,
                displayedNodeStateCount: breakdown[nodeStateField] ?? 0,
                displayedSimcardDataCount: s[simcardDataStateField] ?? 0,
                displayedSimcardSMSCount: s[simcardSMSStateField] ?? 0,
                isLoadingAction: loadingIds.has(s.id),
                genFilterActive: selectedGeneration !== 'all',
            };
        });
    }, [systems, selectedGeneration, nodeStateField, simcardDataStateField, simcardSMSStateField, loadingIds]);

    useEffect(() => {
        const wrapperEl = tableWrapperRef.current?.querySelector<HTMLDivElement>('.p-datatable-wrapper');
        const topEl = topScrollRef.current;
        const topContentEl = topScrollContentRef.current;
        if (!wrapperEl || !topEl || !topContentEl) return;

        const syncWidth = () => {
            topContentEl.style.width = `${wrapperEl.scrollWidth}px`;
        };
        syncWidth();

        let syncing = false;
        const onTopScroll = () => {
            if (syncing) return;
            syncing = true;
            wrapperEl.scrollLeft = topEl.scrollLeft;
            syncing = false;
        };
        const onTableScroll = () => {
            if (syncing) return;
            syncing = true;
            topEl.scrollLeft = wrapperEl.scrollLeft;
            syncing = false;
        };

        const resizeObserver = new ResizeObserver(syncWidth);
        resizeObserver.observe(wrapperEl);
        topEl.addEventListener('scroll', onTopScroll);
        wrapperEl.addEventListener('scroll', onTableScroll);

        return () => {
            resizeObserver.disconnect();
            topEl.removeEventListener('scroll', onTopScroll);
            wrapperEl.removeEventListener('scroll', onTableScroll);
        };
    }, [systemsWithDerivedData, visibleFields]);

    const genFilterActive = selectedGeneration !== 'all';

    const genColumnClassName = (baseClassName?: string) => {
        const classes = [baseClassName, genFilterActive ? 'gen-filtered-col' : undefined].filter(Boolean);
        return classes.length > 0 ? classes.join(' ') : undefined;
    };

    FilterService.register('count_filter', (value, filter) => {
        if (filter === null || filter === undefined) return true;
        if (filter === 'zero') return value === 0;
        if (filter === 'nonzero') return value > 0;
        return true;
    });

    const [filters, setFilters] = useState<typeof DEFAULT_FILTERS>(buildFiltersFromUrl);

    // Keep the URL in sync with filters/sort/pagination/generation/etc so the
    // view is shareable and survives a refresh. replaceState (not pushState)
    // so typing in a filter box doesn't spam the browser back button.
    useEffect(() => {
        const params = new URLSearchParams();
        if (selectedGeneration !== 'all') params.set('gen', selectedGeneration);
        if (nodeStateField !== 'operationalActiveCount') params.set('nodeState', nodeStateField);
        if (simcardDataStateField !== 'simcardsDataCount') params.set('simcardDataState', simcardDataStateField);
        if (simcardSMSStateField !== 'simcardsSMSCount') params.set('simcardSMSState', simcardSMSStateField);
        if (globalFilter) params.set('q', globalFilter);
        if (first) params.set('first', String(first));
        if (rows !== 25) params.set('rows', String(rows));
        if (sortField) params.set('sortField', sortField);
        if (sortOrder) params.set('sortOrder', String(sortOrder));
        if (!sameFieldSet(visibleFields, DEFAULT_VISIBLE_FIELDS)) params.set('cols', visibleFields.join(','));

        Object.keys(filters).forEach((key) => {
            if (key === 'global') return;
            const cur = (filters as any)[key];
            const def = (DEFAULT_FILTERS as any)[key];
            const curVal = cur?.value ?? null;
            const defVal = def?.value ?? null;
            if (curVal !== defVal) {
                params.set(`f_${key}`, typeof curVal === 'boolean' ? (curVal ? 'true' : 'false') : String(curVal));
            }
        });

        const query = params.toString();
        const newUrl = `${window.location.pathname}${query ? '?' + query : ''}${window.location.hash}`;
        window.history.replaceState(null, '', newUrl);
    }, [selectedGeneration, nodeStateField, simcardDataStateField, simcardSMSStateField, globalFilter, first, rows, sortField, sortOrder, filters, visibleFields]);

    const powerOptions = [
        { label: '', value: 'mains', icon: <Power/> },
        { label: '', value: 'battery', icon: <BatteryFull/> },
    ];

    const modeOptions = [
        { label: '', value: 'normal', icon: <Computer/> },
        { label: '', value: 'manual', icon: <PanTool/> },
        { label: '', value: 'switching', icon: <SwapHoriz/> },
    ];

    const boolOptions = [
        { label: '', value: true, icon:  <Check/> },
        { label: '', value: false, icon: <Close/> },
    ];

    const countOptions = [
        { label: '0', value: 'zero' },
        { label: '> 0', value: 'nonzero' },
    ];

    const nodeStateOptions = [
        { label: 'Nonoperational', value: 'nonoperationalCount' },
        { label: 'Operational Active', value: 'operationalActiveCount' },
        { label: 'Operational Inactive', value: 'operationalInactiveCount' },
    ];

    const simcardDataStateOptions = [
        { label: 'Total', value: 'simcardsDataCount' },
        { label: 'Customer', value: 'customerDataSimcardsCount' },
        { label: 'WISENE', value: 'wiseneDataSimcardsCount' },
    ];
    const simcardSMSStateOptions = [
        { label: 'Total', value: 'simcardsSMSCount' },
        { label: 'Customer', value: 'customerSMSSimcardsCount' },
        { label: 'WISENE', value: 'wiseneSMSSimcardsCount' },
    ];
    const THREAT_LEVEL_COLORS: Record<string, string> = {
        none: 'bg-green-500',
        unknown_or_NA: 'bg-green-500',
        L1: 'bg-green-300',
        L2: 'bg-yellow-500',
        L3: 'bg-orange-500',
        L4: 'bg-red-500',
    };  

    const handleSetProblemsConfirmed = async (id: number, confirmed: boolean) => {
        setLoadingIds(prev => new Set(prev).add(id));
        try {
            await setProblemsConfirmed(id, confirmed);
        } catch (e) {
            console.error('Failed to update problems confirmed status', e);
            setLoadingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const headerWithTooltip = (label: React.ReactNode, tooltip: string, position: 'top' | 'left' | 'right' | 'bottom' = 'top') => (
        <span>
            {label}{' '}
            <i 
                className="pi pi-info-circle" 
                data-pr-tooltip={tooltip}
                data-pr-position={position}
                style={{ marginLeft: '4px', fontSize: '0.85em', cursor: 'help' }}
            />
        </span>
    );

    const formatTimestamp = (date: Date) => {
        // "2026-07-24 14:32:10" -> "2026-07-24 14:32"
        return date.toLocaleString('sv-SE').slice(0, 16).replace('T', ' ');
    };

    // W2 systems not synced on-line: outdated sync and measurements (and SMS when off-line) are expected
    const syncAndMeasurementsExpectedOld = (rowData) => ['sms-only', 'off-line'].includes(rowData.syncSetting);
    const smsExpectedOld = (rowData) => rowData.syncSetting === 'off-line';

    const syncTimestampTemplate = (rowData: any) => {
        if (!rowData.syncTimestamp) {
            return null;
        }
        const timestampDate = new Date(rowData.syncTimestamp);
        const dateStr = formatTimestamp(timestampDate);

        const now = new Date();

        const limit1 = new Date(now.getTime() - 72 * 60 * 60 * 1000); // -72h
        const limit2 = new Date(now.getTime() - 24 * 60 * 60 * 1000); // -24h
        const limit3 = new Date(now.getTime() - 6 * 60 * 60 * 1000);  // -6h

        if (rowData.archived) {
            return <div className="timestamp-cell" style={blackDateStyle}>{dateStr}</div>;
        }

        const problemsConfirmedNow = rowData.problemsConfirmedUntil && new Date(rowData.problemsConfirmedUntil) > new Date();
        if (problemsConfirmedNow) {
            return <div className="timestamp-cell" style={grayDateStyle}>{dateStr}</div>;
        }

        if (syncAndMeasurementsExpectedOld(rowData) && timestampDate < limit3) {
            return <div className="timestamp-cell" style={grayDateStyle}>{dateStr}</div>;
        }

        if (timestampDate < limit1) {
            return <div className="timestamp-cell" style={redDateStyle}>{dateStr}</div>;
        }

        if (timestampDate < limit2) {
            return <div className="timestamp-cell" style={orangeDateStyle}>{dateStr}</div>;
        }

        if (timestampDate < limit3) {
            return <div className="timestamp-cell" style={yellowDateStyle}>{dateStr}</div>;
        }

        return <div className="timestamp-cell">{dateStr}</div>;
    };


    const lastMeasurementTimestampTemplate = (rowData) => {
        if (!rowData.lastMeasurementTimestamp) {
            return null;
        } 
        const timestampDate = new Date(rowData.lastMeasurementTimestamp);
        const dateStr = formatTimestamp(timestampDate);

        const now = new Date();

        const limit = new Date(now.getTime() - 12 * 60 * 60 * 1000); // -12h
        const expectedOld = syncAndMeasurementsExpectedOld(rowData);

        if (rowData.archived) {
            return <div className="timestamp-cell" style={blackDateStyle}>{dateStr}</div>;
        }

        const problemsConfirmedNow = rowData.problemsConfirmedUntil && new Date(rowData.problemsConfirmedUntil) > new Date();
        if (problemsConfirmedNow || (expectedOld && timestampDate < limit)) {
            return <div className="timestamp-cell" style={grayDateStyle}>{dateStr}</div>;
        }
        if (timestampDate < limit) {
            return <div className="timestamp-cell" style={redDateStyle}>{dateStr}</div>;
        }

        return <div>{dateStr}</div>;
    };

    const lastSMSTimestampTemplate = (rowData) => {
        if (!rowData.lastSMSTimestamp) {
            return null;
        } 
        const timestampDate = new Date(rowData.lastSMSTimestamp);
        const dateStr = formatTimestamp(timestampDate);

        const now = new Date();

        const limit = new Date(now.getTime() - 26 * 60 * 60 * 1000); // -26h
        const expectedOld = smsExpectedOld(rowData);

        if (rowData.archived) {
            return <div className="timestamp-cell" style={blackDateStyle}>{dateStr}</div>;
        }

        const problemsConfirmedNow = rowData.problemsConfirmedUntil && new Date(rowData.problemsConfirmedUntil) > new Date();
        if (problemsConfirmedNow || (expectedOld && timestampDate < limit)) {
            return <div className="timestamp-cell" style={grayDateStyle}>{dateStr}</div>;
        }
        if (timestampDate < limit) {
            return <div className="timestamp-cell" style={redDateStyle}>{dateStr}</div>;
        }

        return <div>{dateStr}</div>;
    };

    const measurementPeriodTemplate = (rowData) => {
        if (!rowData.measurementPeriod) {
            return null;
        }

        const totalSeconds = rowData.measurementPeriod;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        let result = '';

        if (hours > 0) {
            result += `${hours}h`;
        }
        if (minutes > 0) {
            result += `${minutes}m`;
        }
        if (hours === 0 && minutes === 0) {
            result += `${seconds}s`;
        }
        if (totalSeconds < 3600) {
            return <Tag 
                value={result} 
                className={`px-2 py-1 rounded bg-yellow-500 text-white`} 
            />
        }
        return result;

    };

    const BATTERY_STATUS_COLORS: Record<string, string> = {
        battery_good_normal: 'bg-green-500 text-white',
        battery_weak: 'bg-orange-500 text-white',
        battery_empty: 'bg-red-500 text-white',
    };

    const minBatteryPercentageTemplate = (rowData) => {
        if (rowData.minBatteryPercentage === null || rowData.minBatteryPercentage === undefined) {
            return null;
        }
        const colorClass = BATTERY_STATUS_COLORS[rowData.worstBatteryStatus];
        if (!colorClass) {
            return `${rowData.minBatteryPercentage}%`;
        }
        return <Tag value={`${rowData.minBatteryPercentage}%`} className={`px-2 py-1 rounded ${colorClass}`} />;
    };

    const maxDeflectionTemplate = (rowData) => {
        if (rowData.maxDeflection == null || rowData.maxDeflection == undefined) {
            return null;
        }

        const maxStr = rowData.maxDeflection < 1 && rowData.maxDeflection > -1 
            ? '<1%' 
            : `${round(rowData.maxDeflection, 0)}%`;

        const colorClass = THREAT_LEVEL_COLORS[rowData.highestThreatLevel];

        return <Tag value={maxStr} className={`px-2 py-1 rounded ${colorClass} text-white`} />;
    };

    const maxDeflectionSortFunction = (event: { data: any[], order: number }) => {
        const { data, order } = event;
        return [...data].sort((a, b) => {
            const valA = a.maxDeflection ?? -Infinity;
            const valB = b.maxDeflection ?? -Infinity;
            return (Math.abs(valA) - Math.abs(valB)) * order;
        });
    };

    const obstructedCountTemplate = (rowData) => {
        if (rowData.obstructedCount == null || rowData.obstructedCount == undefined) {
            return null;
        }

        if (rowData.obstructedCount > 0) {
            return <Tag value={rowData.obstructedCount} className={`px-2 py-1 rounded bg-purple-500 text-white`} />;
        }
        return rowData.obstructedCount;
    };

    const rainstormModeNowTemplate = (rowData) =>
        <Tag
            value={rowData.rainstormModeNow ? <Check/> : <Close/>}
            className={`px-2 py-1 rounded ${ rowData.rainstormModeNow ? 'bg-green-500 text-white' : 'bg-gray-600 text-white'}`}
        />;

    const dailyMeasurementTemplate = (rowData) => {
        if (rowData.dailyMeasurementOn === false) {
            return 'OFF';
        }
        if (!rowData.dailyMeasurementTime) {
            return null;
        }
        // "0000-01-01T14:00:00Z"-ish (a bare TIME column serialized as a zero-date timestamp) -> "14:00"
        const timePart = rowData.dailyMeasurementTime.split('T')[1];
        return timePart ? timePart.slice(0, 5) : rowData.dailyMeasurementTime;
    };

    const maintenanceProblemsTemplate = (rowData) => {
        // null: not a W2 system (or no problems data at all) - the check doesn't apply
        if (!rowData.maintenanceProblems) {
            return null;
        }
        const problems: string[] = rowData.maintenanceProblems;
        const hasProblems = problems.length > 0;
        return (
            <Build
                style={{ color: hasProblems ? '#c0392b' : '#9e9e9e', cursor: 'help' }}
                data-pr-tooltip={hasProblems ? problems.join('\n') : 'No maintenance problems'}
                data-pr-position="left"
            />
        );
    };

    const maintenanceProblemsSortFunction = (event: { data: any[], order: number }) => {
        const { data, order } = event;
        return [...data].sort((a, b) => {
            // not-W2 (null) sorts below a W2 system with zero problems
            const lenA = a.maintenanceProblems ? a.maintenanceProblems.length : -1;
            const lenB = b.maintenanceProblems ? b.maintenanceProblems.length : -1;
            return (lenA - lenB) * order;
        });
    };

    const exportValueFormatter = (e: { data: any; field: string; rowData: any }) => {
        switch (e.field) {
            case 'dailyMeasurementTime': {
                if (e.rowData.dailyMeasurementOn === false) return 'OFF';
                if (!e.data) return '';
                const timePart = String(e.data).split('T')[1];
                return timePart ? timePart.slice(0, 5) : e.data;
            }
            case 'maintenanceProblems':
                return Array.isArray(e.data) ? e.data.join('\n') : (e.data ?? '');
            case 'maxDeflection': {
                if (e.data === null || e.data === undefined) return '';
                return Math.abs(e.data) < 1 ? '<1%' : `${round(e.data, 0)}%`;
            }
            case 'measurementPeriod': {
                if (!e.data) return '';
                const totalSeconds = e.data;
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;
                if (hours > 0) return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
                if (minutes > 0) return `${minutes}m`;
                return `${seconds}s`;
            }
            default:
                return e.data;
        }
    };

    const nodeStateFilterElement = () => (
        <Dropdown
            value={nodeStateField}
            options={nodeStateOptions}
            onChange={(e) => { setNodeStateField(e.value); }}
            placeholder="-"
            className="w-full"
        />
    );

    const simcardDataStateFilterElement = () => (
        <Dropdown
            value={simcardDataStateField}
            options={simcardDataStateOptions}
            onChange={(e) => { setSimcardDataStateField(e.value); }}
            placeholder="-"
            className="w-full"
        />
    );
    const simcardSMSStateFilterElement = () => (
        <Dropdown
            value={simcardSMSStateField}
            options={simcardSMSStateOptions}
            onChange={(e) => { setSimcardSMSStateField(e.value); }}
            placeholder="-"
            className="w-full"
        />
    );


    const enumFilter = (field, options, matchMode = FilterMatchMode.EQUALS) => {
        const selectedOption = options.find(o => o.value === filters[field]?.value);
        return (
            <Dropdown
                value={filters[field]?.value}
                options={options}
                onChange={(e) => {
                    setFilters((prev) => ({
                        ...prev,
                        [field]: { value: e.value, matchMode: matchMode },
                    }))
                }}
                itemTemplate={(option) => (
                    <div className="flex align-items-center gap-2">
                        {option.icon}
                        <span>{option.label}</span>
                    </div>
                )}
                valueTemplate={() =>
                    selectedOption
                        ? <div className="flex align-items-center gap-2">
                            {selectedOption.icon}
                            <span>{selectedOption.label}</span>
                        </div>
                        : <span>-</span>
                }
                placeholder="-"
                showClear
                className="w-full"
            />
        );
    };

    const handleResetView = () => {
        setFilters(DEFAULT_FILTERS);
        setGlobalFilter(null);
        setSelectedGeneration('all');
        setNodeStateField('operationalActiveCount');
        setSimcardDataStateField('simcardsDataCount');
        setSimcardSMSStateField('simcardsSMSCount');
        setFirst(0);
        setRows(25);
        setSortField(undefined);
        setSortOrder(undefined);
        getSystemsOverview(); // fresh data from db with default order - dispatches GET_SYSTEMS_FOR_TABLE_START itself
    };

    const actionBodyTemplate = (rowData) => {
        const problemsConfirmedNow = rowData.problemsConfirmedUntil && new Date(rowData.problemsConfirmedUntil) > new Date();

        return (
            <>
                <Button
                    icon={problemsConfirmedNow ? "pi pi-times" : "pi pi-check"}
                    rounded outlined className="mr-2"
                    loading={rowData.isLoadingAction}
                    disabled={rowData.isLoadingAction || rowData.archived}
                    onClick={() => handleSetProblemsConfirmed(rowData.id, !problemsConfirmedNow)}
                />
                <Link to={`/systems/${rowData.id}/details`}>
                    <Button 
                        icon="pi pi-eye"
                        rounded outlined className="mr-2"
                    />
                </Link>
            </>
        );
    };

    const columnToggle = (
        <div style={{ marginBottom: '0.5rem' }}>
            <MultiSelect
                value={visibleFields}
                options={TOGGLEABLE_COLUMNS.map(c => ({ label: c.label, value: c.field }))}
                onChange={(e) => setVisibleFields(e.value)}
                placeholder="Columns"
                display="chip"
                filter
                className="w-full md:w-20rem"
                style={{ maxWidth: '400px' }}
            />
        </div>
    );

    const generationSelector = (
        <div style={{ marginBottom: '0.7rem' }}>
            <Dropdown
                value={selectedGeneration}
                options={GENERATION_OPTIONS}
                onChange={(e) => setSelectedGeneration(e.value)}
                className={`w-full md:w-14rem gen-filter-dropdown ${genFilterActive ? 'gen-filter-dropdown-active' : ''}`}
                style={{ minWidth: '250px' }}
            />
        </div>
    );

    const resetViewButton = (
        <div style={{ marginBottom: '0.5rem' }}>
            <Button
                icon="pi pi-refresh"
                label="Reset & Refresh"
                outlined
                onClick={handleResetView}
            />
        </div>
    );

    const dataTable = 
            <div className="card">
                <Tooltip target="[data-pr-tooltip]" style={{ maxWidth: '300px' }} />
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    {columnToggle}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {generationSelector}
                        {resetViewButton}
                        <div style={{ marginBottom: '0.5rem' }}>
                            <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'systems')} />
                        </div>
                    </div>
                </div>
                <div ref={topScrollRef} className="systems-table-top-scroll">
                    <div ref={topScrollContentRef} style={{ height: 1 }} />
                </div>
                <div className="systems-table-dense" ref={tableWrapperRef}>
                <DataTable ref={dt} value={systemsWithDerivedData} exportFilename="systems"
                        dataKey="id"  paginator first={first} rows={rows} rowsPerPageOptions={[10, 25, 50, 100]}
                        onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                        sortField={sortField} sortOrder={sortOrder}
                        onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder as 1 | -1 | undefined); }}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} systems" globalFilter={globalFilter} 
                        filters={filters} onFilter={(e) => setFilters(e.filters as typeof DEFAULT_FILTERS)} filterDisplay="row" stripedRows size='small' resizableColumns scrollable scrollHeight="flex"
                        scrollDirection="horizontal" columnResizeMode="expand" exportFunction={exportValueFormatter}>
               <Column 
                    field="name" 
                    header="Name"
                    sortable
                    body={(rowData) => (<div style={{paddingTop: '10px', paddingBottom: '10px', display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                        <Link to={`/systems/${rowData.id}/dashboard`} className="text-primary">{rowData.name}</Link>
                        {rowData.vpn2Ip && (
                            <Button
                                icon="pi pi-external-link"
                                rounded text severity="secondary"
                                tooltip={`https://${rowData.vpn2Ip}`}
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => window.open(`https://${rowData.vpn2Ip}`, '_blank', 'noopener,noreferrer')}
                                style={{ width: '1.75rem', height: '1.75rem' }}
                            />
                        )}
                        </div>
                    )}
                    style={{ minWidth: '200px' }}
                    className="system-cell"
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                {visibleFields.includes('syncTimestamp') && (
                <Column 
                    field="syncTimestamp"
                    header={headerWithTooltip('Sync', "WSN: When wmc-client last sent the XML\n\nLoRa: Oldest of all devices' latest packet timestamps", 'bottom')}
                    exportHeader="Sync"
                    body={syncTimestampTemplate}
                    style={{ width: '50px' }}
                    className="timestamp-cell"
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('lastMeasurementTimestamp') && (
                <Column 
                    field="lastMeasurementTimestamp"
                    header={headerWithTooltip('Last m.', "Oldest of all devices' latest measurement timestamps", 'bottom')}
                    exportHeader="Last m."
                    body={lastMeasurementTimestampTemplate}
                    style={{ width: '50px' }}
                    className="timestamp-cell"
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('lastSMSTimestamp') && (
                <Column 
                    field="lastSMSTimestamp"
                    header={headerWithTooltip('Last SMS', 'When the system last sent SMS', 'bottom')}
                    exportHeader="Last SMS"
                    body={lastSMSTimestampTemplate}
                    style={{ width: '50px' }}
                    className="timestamp-cell"
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('lostDevicesCount') && (
                <Column
                    key={`lostDevicesCount-${genFilterActive}`}
                    field="lostDevicesCount"
                    header="Lost"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('lostDevicesCount', countOptions, 'count_filter')}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('sensorErrorsCount') && (
                <Column 
                    field="sensorErrorsCount"
                    header={headerWithTooltip('Sens. err.', "Number of devices that reported a sensor error in the last 24 hours\n\nNote: if a device hasn't reported in, its sensor error status may be outdated.", "left")}
                    exportHeader="Sens. err."
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('sensorErrorsCount', countOptions, 'count_filter')}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('sensorErrorNodesCount') && (
                <Column 
                    field="sensorErrorNodesCount"
                    header={headerWithTooltip('Sens. err. cnt', "Number of devices whose sensor error counter is not zero, i.e. which reported a sensor error since the counter was last reset", "left")}
                    exportHeader="Sens. err. cnt"
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('sensorErrorNodesCount', countOptions, 'count_filter')}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('mode') && (
                <Column 
                    field="mode" 
                    header="Mode"
                    body={(rowData) =>
                        <Tag 
                        value={rowData.mode == 'normal' ? <Computer/> : ( rowData.mode == 'manual' ? <PanTool/> : <SwapHoriz/> )} 
                        className={`px-2 py-1 rounded ${ rowData.mode == 'normal' ? 'bg-green-500 text-white' : (rowData.mode == 'manual' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white')}`} 
                        />
                    }
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={enumFilter('mode', modeOptions)}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('power') && (
                <Column 
                    field="power" 
                    header="Power"
                    body={(rowData) =>
                        <Tag 
                        value={rowData.power == 'mains' ? <Power/> : <BatteryFull/>} 
                        className={`px-2 py-1 rounded ${ rowData.power == 'mains' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`} 
                        />
                    }
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    filterField="power"
                    showFilterMenu={false}
                    filterElement={(options) => enumFilter('power', powerOptions)}
                    sortable>
                </Column>
                )}
                {visibleFields.includes('minBatteryPercentage') && (
                <Column
                    field="minBatteryPercentage"
                    header="Battery"
                    body={minBatteryPercentageTemplate}
                    style={{ width: '30px' }}
                    sortable>
                </Column>
                )}
                {visibleFields.includes('maxDeflection') && (
                <Column
                    field="maxDeflection"
                    header="Defl."
                    body={maxDeflectionTemplate}
                    style={{ width: '30px' }}
                    sortable
                    sortFunction={maxDeflectionSortFunction}>
                </Column>
                )}
                {visibleFields.includes('maintenanceProblems') && (
                <Column
                    field="maintenanceProblems"
                    header={headerWithTooltip(<Build/>, 'Maintenance problems\n\nHover the wrench for details.\n\nWSN only.', 'bottom')}
                    exportHeader="Maintenance problems"
                    body={maintenanceProblemsTemplate}
                    style={{ width: '30px' }}
                    sortable
                    sortFunction={maintenanceProblemsSortFunction}>
                </Column>
                )}
                {visibleFields.includes('measurementPeriod') && (
                <Column 
                    field="measurementPeriod" 
                    header="M. Per."
                    body={measurementPeriodTemplate}
                    style={{ width: '30px' }}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('l1Count') && (
                <Column
                    key={`l1Count-${genFilterActive}`}
                    field="l1Count"
                    header="L1"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('l1Count', countOptions, 'count_filter')}
                    >    
                </Column>
                )}
                {visibleFields.includes('l2Count') && (
                <Column
                    key={`l2Count-${genFilterActive}`}
                    field="l2Count"
                    header="L2"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('l2Count', countOptions, 'count_filter')}
                    >    
                </Column>
                )}
                {visibleFields.includes('l3Count') && (
                <Column
                    key={`l3Count-${genFilterActive}`}
                    field="l3Count"
                    header="L3"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('l3Count', countOptions, 'count_filter')}
                    >    
                </Column>
                )}
                {visibleFields.includes('l4Count') && (
                <Column
                    key={`l4Count-${genFilterActive}`}
                    field="l4Count"
                    header="L4"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('l4Count', countOptions, 'count_filter')}
                    >    
                </Column>
                )}
                {visibleFields.includes('obstructedCount') && (
                <Column
                    key={`obstructedCount-${genFilterActive}`}
                    field="obstructedCount"
                    header="Obstr."
                    body={obstructedCountTemplate}
                    style={{ width: '25px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('obstructedCount', countOptions, 'count_filter')}
                    >               
                </Column>
                )}
                {visibleFields.includes('operationalActiveCount') && (
                <Column
                    key={`displayedNodeStateCount-${genFilterActive}`}
                    field="displayedNodeStateCount"
                    header="Op./Act."
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={nodeStateFilterElement}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('mdiCount') && (
                <Column
                    key={`mdiCount-${genFilterActive}`}
                    field="mdiCount"
                    header="MDI"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('mdlCount') && (
                <Column
                    key={`mdlCount-${genFilterActive}`}
                    field="mdlCount"
                    header="MDL"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('iadCount') && (
                <Column
                    key={`iadCount-${genFilterActive}`}
                    field="iadCount"
                    header="IAD"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('adCount') && (
                <Column
                    key={`adCount-${genFilterActive}`}
                    field="adCount"
                    header="AD"
                    style={{ width: '20px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('deviceCount') && genFilterActive && (
                <Column
                    key={`deviceCount-${genFilterActive}`}
                    field="deviceCount"
                    header={headerWithTooltip('Gen Dev.', "Number of devices belonging to the selected generation", "bottom")}
                    exportHeader="Gen Dev."
                    style={{ width: '30px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('serviceModeNow') && (
                <Column 
                    field="serviceModeNow" 
                    header="S. Mode"
                 body={(rowData) =>
                        <Tag 
                        value={rowData.serviceModeNow  ? <Check/> : <Close/>} 
                        className={`px-2 py-1 rounded ${ rowData.serviceModeNow ? 'bg-green-500 text-white' : 'bg-gray-600 text-white'}`}
                        />
                    }
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={enumFilter('serviceModeNow', boolOptions)}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('overdueMeasurementsCount') && (
                <Column
                    key={`overdueMeasurementsCount-${genFilterActive}`}
                    field="overdueMeasurementsCount"
                    header={headerWithTooltip('Ov. M.', "Overdue measurements:\nNumber of devices that haven't taken a measurement in the last 12 hours", "bottom")}
                    exportHeader="Ov. M."
                    style={{ width: '30px' }}
                    className={genColumnClassName()}
                    bodyClassName={(rowData) => rowData.genFilterActive ? 'gen-filtered-col' : undefined}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => enumFilter('overdueMeasurementsCount', countOptions, 'count_filter')}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('notificationsEmailOn') && (
                <Column 
                    field="notificationsEmailOn" 
                    header="E-mail"
                    body={(rowData) =>
                        <Tag 
                        value={rowData.notificationsEmailOn  ? <Check/> : <Close/>} 
                        className={`px-2 py-1 rounded ${ rowData.notificationsEmailOn ? 'bg-green-500 text-white' : 'bg-gray-600 text-white'}`}
                        />
                    }
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={enumFilter('notificationsEmailOn', boolOptions)}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('notificationsPushOn') && (
                <Column 
                    field="notificationsPushOn" 
                    header="Push"
                    body={(rowData) =>
                        <Tag 
                        value={rowData.notificationsPushOn  ? <Check/> : <Close/>} 
                        className={`px-2 py-1 rounded ${ rowData.notificationsPushOn ? 'bg-green-500 text-white' : 'bg-gray-600 text-white'}`}
                        />
                    }
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={enumFilter('notificationsPushOn', boolOptions)}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('notificationsSmsOn') && (
                <Column 
                    field="notificationsSmsOn" 
                    header="SMS"
                    body={(rowData) =>
                        <Tag 
                        value={rowData.notificationsSmsOn  ? <Check/> : <Close/>} 
                        className={`px-2 py-1 rounded ${ rowData.notificationsSmsOn ? 'bg-green-500 text-white' : 'bg-gray-600 text-white'}`} 
                        />
                    }
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={enumFilter('notificationsSmsOn', boolOptions)}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('routersCount') && (
                <Column 
                    field="routersCount"
                    header="Routers"
                    style={{ width: '30px' }}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('simcardsDataCount') && (
                <Column 
                    field="displayedSimcardDataCount"
                    header="SIM cards Data"
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={simcardDataStateFilterElement}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('simcardsSMSCount') && (
                <Column 
                    field="displayedSimcardSMSCount"
                    header="SIM cards SMS"
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={simcardSMSStateFilterElement}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('en91Count') && (
                <Column 
                    field="en91Count"
                    header="AA"
                    style={{ width: '30px' }}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('en92Count') && (
                <Column 
                    field="en92Count"
                    header="AAA"
                    style={{ width: '30px' }}
                    sortable>               
                </Column>
                )}
                {visibleFields.includes('en95Count') && (
                <Column
                    field="en95Count"
                    header="D"
                    style={{ width: '30px' }}
                    sortable>
                </Column>
                )}
                {visibleFields.includes('rainstormModeNow') && (
                <Column
                    field="rainstormModeNow"
                    header={headerWithTooltip(<Opacity/>, 'Rainstorm mode active', 'bottom')}
                    exportHeader="Rainstorm mode"
                    body={rainstormModeNowTemplate}
                    style={{ width: '30px' }}
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={enumFilter('rainstormModeNow', boolOptions)}
                    sortable>
                </Column>
                )}
                {visibleFields.includes('dailyMeasurement') && (
                <Column
                    field="dailyMeasurementTime"
                    header="Daily meas."
                    body={dailyMeasurementTemplate}
                    style={{ width: '30px' }}
                    sortable>
                </Column>
                )}
                <Column body={actionBodyTemplate} exportable={false} style={{ width: '30px' }}></Column>
                </DataTable>
                </div>
            </div>;

    if (systemsForTableLoading !== false && (!systems || systems.length === 0)) {
        return <MapLoading />;
    }

    return (
        <div>
            {dataTable}
        </div>
    )

}


const mapStateToProps = (state) => {
    return {
        systems: state.dashboard.systemsForTable,
        systemsForTableLoading: state.dashboard.systemsForTableLoading,
    };
};

const actions = {
    getSystemsOverview,
    clearSystemsForTable,
    setProblemsConfirmed
};

export default connect(mapStateToProps, actions)(SystemsTable);
